import { useState } from "react";
import { useModal } from "../../context/ModalContext";
import { useRouter } from "next/router";
import { toast } from "react-hot-toast";
import axios from "axios";
import { requireIdToken } from "../../utils/firebaseAuth";
import { Button, SecondaryButton } from "@jammable/ui-core";
import { Spinner } from "@jammable/ui-components/Loader";
import { Shield } from "lucide-react";
import { FaUserShield } from "react-icons/fa";

const BlockUserModal = ({ user, isBlocked = false, redirectUrl }) => {
  const [loading, setLoading] = useState(false);
  const { closeModal } = useModal();
  const router = useRouter();

  const handleBlock = async () => {
    try {
      setLoading(true);
      const token = await requireIdToken();

      await axios.post(
        "/api/user/blockUser",
        { targetUserId: user?.firebaseUid },
        { headers: { Authorization: token } },
      );

      toast.success(`User ${isBlocked ? "unblocked" : "blocked"} successfully`);
      closeModal("block-user-modal");
      router.push(redirectUrl);
    } catch (error) {
      console.error("Error blocking user:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      <div className="tw-flex tw-items-center tw-gap-3">
        <div className="tw-bg-red-100 tw-p-2 tw-rounded-full tw-size-12 tw-flex tw-items-center tw-justify-center">
          <FaUserShield className="tw-w-6 tw-h-6 tw-text-red-500" />
        </div>
        <div className="tw-text-xl tw-font-black">
          {isBlocked ? `Unblock ${user?.username}` : `Block ${user?.username}`}
        </div>
      </div>

      <div className=" tw-leading-relaxed">
        {isBlocked ? (
          <div>
            <div className="tw-font-semibold">
              Unblocking this user will allow them to:
            </div>
            <ul className="tw-list-disc tw-pl-5 tw-mt-2 tw-space-y-1">
              <li>Send you messages</li>
              <li>View your profile and content</li>
              <li>Interact with your covers</li>
            </ul>
          </div>
        ) : (
          <>
            <div className="tw-mb-3 tw-font-semibold">
              We don&apos;t tolerate harassment, hate speech, or abusive
              behavior on Jammable. Blocking this user will:
            </div>
            <ul className="tw-list-disc tw-pl-5 tw-space-y-1">
              <li>Prevent them from messaging you</li>
              <li>Restrict their access to your profile</li>
              <li>Stop them from interacting with your content</li>
            </ul>
            <div className="tw-mt-3 tw-text-sm tw-text-black/50 tw-font-semibold">
              If you&apos;re experiencing harassment, we encourage you to report
              the user in addition to blocking them.
            </div>
          </>
        )}
      </div>

      <div className="tw-flex tw-gap-2 tw-mt-2">
        <SecondaryButton
          onClick={() => closeModal("block-user-modal")}
          className="tw-flex-1 tw-rounded-full"
        >
          Cancel
        </SecondaryButton>
        <Button
          onClick={handleBlock}
          className="tw-flex-1 tw-rounded-full tw-bg-red-500 tw-text-white tw-border-none hover:tw-bg-red-600"
          disabled={loading}
        >
          {loading ? (
            <Spinner size={4} className="tw-border-white tw-border-3" />
          ) : isBlocked ? (
            "Unblock User"
          ) : (
            "Block User"
          )}
        </Button>
      </div>
    </div>
  );
};

export default BlockUserModal;
